import { CategoryReference, TagCategory } from 'apprise-core/category';
import { LifecycleEvent } from 'apprise-core/common';
import { noRef, Tag, TagReference } from 'apprise-core/tag';
import { Optional } from 'apprise-core/utils';
import { usePreload } from 'apprise-frontend-core/client/preload';
import { useCategoryStore } from 'apprise-frontend-tags/category/store';
import { tagPlural, tagSingular } from 'apprise-frontend-tags/constants';
import { useExternalTagStore } from 'apprise-frontend-tags/external/store';
import { useTagClientCache } from 'apprise-frontend-tags/tag/context';
import { useCrud } from 'apprise-ui/utils/crudtask';
import { useCategoryModel } from '../category/model';
import { tagapi, useTagCalls } from './clients';
import { useTagModel } from './model';


export const useTagStore = () => {

    const state = useTagClientCache()


    const preload = usePreload()

    const external = useExternalTagStore()

    const crud = useCrud({ singular: tagSingular, plural: tagPlural })

    const model = useTagModel()
    const calls = useTagCalls()

    const catstore = useCategoryStore()
    const catmodel = useCategoryModel()

    const self = {


        allTags: () => state.get().all

        ,

        allTagsOfCategory: (ref: CategoryReference | undefined, onlyactive = false) => self.allTags().filter(tag => tag.category === ref && (onlyactive ? tag.lifecycle.state === 'active' : true))

        ,

        lookupTag: (tag: Optional<TagReference>) => tag ? self.allTags().find(t => t.id === tag) ?? external.lookupById(tag) : undefined

        ,

        safeLookupTag: (tag?: Optional<TagReference>) => self.lookupTag(tag) ?? model.noTag(tag)

        ,


        fetchAllTags: crud.fetchAllWith(async () => {

            const all = await (preload.get<Tag[]>(tagapi) ?? calls.fetchAllTags())

            state.set(s => s.all = all)

            return all

        }).done()

        ,

        saveTag: crud.saveOneWith(async (tag: Tag) => {

            const isNew = !tag.lifecycle.created

            const saved = isNew ? await calls.addTag(tag) : await calls.updateTag(tag)

            state.set(s => s.all = isNew ? [saved, ...s.all] : s.all.map(t => t.id === saved.id ? saved : t))

            return saved

        }).done()

        ,

        removeTag: crud.removeOneWith(async (tag: Tag, onConfirm?: () => void) => {

            await calls.deleteTag(tag)

            state.set(s => s.all = s.all.filter(c => c.id !== tag.id))

            onConfirm?.()

        }).withConsent()

        ,



        //  groups all known tags by category, but - unless the field is to be explicitly ignored - 
        //  the tags of categories that don't map to fields are put in the no-category group.
        allTagsByCategory: (type: Optional<CategoryReference>, ignoreField?: boolean) => {

            // prelim indexing by category
            const catmap: { [key: string]: Tag[] } = self.allTags().reduce((acc, next) => {

                const cat = next.category && (ignoreField || catstore.safeLookupCategory(next.category).properties.field?.enabled) ? next.category : noRef

                return { ...acc, [cat]: [next, ...(acc[cat] ?? [])] }

            }, {})


            return [...catstore.allCategoriesOf(type), catmodel.noCategory()].map(c => ({ category: c, categoryTags: catmap[c.id] }))

        }

        ,

        removeTagsInCategory: (category: string | TagCategory | undefined) => state.set(s => s.all = s.all.filter(tag => tag.category !== (typeof category === 'string' ? category : category?.id)))

        ,

        push: (event: LifecycleEvent<Tag>) => {

            const { subject: pushed } = event

            state.set(s => {

                switch (event.type) {

                    case 'add': s.all = [...self.allTags(), pushed]; break;
                    case 'remove': s.all = s.all.filter(c => c.id !== pushed.id); break;
                    case 'change': s.all = s.all.map(c => c.id === pushed.id ? pushed : c); break;

                }

            })


        }
    }

    return self;

}